<template>
  <div ref="componentRef" :class="['custom-input']">
    <label :for="id" v-if="label">{{ label }}</label>
    <div class="input-wrapper">
      <input
        :id="id"
        :type="inputType"
        :placeholder="placeholder"
        v-model="modelValue"
        :maxlength="maxlength"
        :minlength="minlength"
        :required="required"
        @input="updateValue"
        @paste="handlePaste"
        @focus="handleFocus"
        @blur="handleBlur"
        :style="inputStyle"
        :autofocus="autofocus"
        :inputmode="onlyNumber ? 'numeric' : undefined" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, onBeforeUnmount } from 'vue'
import { info, defaultProps } from './config'
import useAppStore from '@/h5/stores/app'
import { computeRatioPx } from '@/packages/plugins/utils'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)
const inputStyle = ref()
const uuid = ref()
const modelValue = ref(props.modelValue)

const appStore = useAppStore ? useAppStore() : null

watch(
  () => [props.element, props.textAlign],
  ([val, twoVal]) => {
    const style = val.style
    inputStyle.value = {
      fontSize: style.fontSize ? computeRatioPx(style.fontSize) : computeRatioPx(32),
      fontWeight: style.fontWeight || 300,
      lineHeight: style.lineHeight,
      letterSpacing: style.letterSpacing,
      color: style.color || '#000',
      backgroundColor: style.backgroundColor,
      opacity: style.opacity || 'none',
      textAlign: twoVal
    }
  },
  { immediate: true, deep: true }
)

const emits = defineEmits(['update:modelValue', 'focus', 'blur'])
const id = `input-${Math.random().toString(36).substr(2, 9)}`
const inputType = ref(props.type)
const componentRef = ref()
let intersectionObserver = null

const handleFocus = () => {
  emits('focus')
}

const handleBlur = async () => {
  emits('blur')
}

const filterNumericValue = (value) => {
  if (!props.onlyNumber) return value
  return String(value ?? '').replace(/[^\d]/g, '')
}

const syncInputValue = (value) => {
  modelValue.value = value
  emits('update:modelValue', value)
  appStore && appStore.setPageDataSource(uuid.value, { value: value || '' })
}

const updateValue = (event) => {
  const value = filterNumericValue(event.target.value)
  if (event.target.value !== value) {
    event.target.value = value
  }
  syncInputValue(value)
}

const handlePaste = (event) => {
  if (!props.onlyNumber) return
  event.preventDefault()
  const pastedText = (event.clipboardData || window.clipboardData).getData('text')
  const filteredText = filterNumericValue(pastedText)
  document.execCommand('insertText', false, filteredText)
  syncInputValue(filterNumericValue(event.target.value))
}

/** 设置输入框的值 */
const setValue = (params) => {
  const value = filterNumericValue(params?.value ?? '')
  syncInputValue(value)
}

const initObserver = () => {
  if (componentRef.value) {
    // 监听组件是否进入视口
    intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        console.log('entry.isIntersecting', entry.isIntersecting)
        if (entry.isIntersecting) {
          modelValue.value = ''
          emits('update:modelValue', '')
          appStore && appStore.setPageDataSource(uuid.value, { value: '' })
          props.autofocus && componentRef.value.querySelector('input').focus()
        }
      })
    })
    intersectionObserver.observe(componentRef.value)
  }
}

onMounted(() => {
  nextTick(() => {
    if (componentRef.value) {
      uuid.value = componentRef.value.getAttribute('id') || ''
      initObserver()
    }
  })
})

onBeforeUnmount(() => {
  if (intersectionObserver) {
    intersectionObserver.disconnect()
  }
})

defineExpose({
  setValue
})
</script>

<style scoped>
.custom-input {
  display: flex;
  flex-direction: column;
}

.input-wrapper {
  position: relative;
  height: 100%;
}

input {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  outline: none;
  border: none;
  transition: border-color 0.3s;
  background-color: transparent;
}

input:focus {
  border-color: #007bff;
}

input.error {
  border-color: red;
}
</style>
